// src/pages/Privacy.js

import React from "react";
import { Helmet } from "react-helmet";
import "../styles/Privacy.css";

const Privacy = () => {
  return (
    <div className="privacy-page page">
      {/* SEO Metadata */}
      <Helmet>
        <title>Privacy Policy - Ghrianghraffy</title>
        <meta
          name="description"
          content="Privacy Policy for Ghrianghraffy, explaining how we collect, use, and protect personal data for schools, staff and parents across our website and mobile apps."
        />
      </Helmet>

      <div className="privacy-card">
        <header className="privacy-header">
          <p className="privacy-kicker">Privacy</p>
          <h1 className="privacy-title">Privacy Policy</h1>
          <p className="privacy-meta">Last updated: December 8, 2025</p>
        </header>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Introduction</h2>
          <p>
            Welcome to Ghrianghraffy, an app and web service for School
            Principals, School Staff, and authorised Parents to capture, upload,
            and view their school&apos;s photos securely and quickly. Parents
            have view-only access. We prioritise your privacy by helping to
            minimise how long school photos remain on personal devices and by
            giving schools tools to manage access and remove data when needed.
          </p>
          <p>
            This Privacy Policy explains how we collect, use, disclose, and
            protect your information when you use our services, including:
          </p>
          <ul>
            <li>Our public website (e.g. marketing pages);</li>
            <li>Our web dashboard for Principals and Staff;</li>
            <li>Our mobile apps (for example, the Ghrianghraffy React Native app);</li>
            <li>
              Any integrations we provide, such as plugins used on school
              websites to show approved photos.
            </li>
          </ul>
          <p>
            By using Ghrianghraffy, you agree to the practices described in this
            policy. If you do not agree, please do not use the service.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Information We Collect</h2>
          <p>We collect the following types of information:</p>
          <ul>
            <li>
              <strong>Account and Profile Information</strong>: When you log in,
              we collect your email, a password (stored only in hashed form on
              our servers), and authentication tokens stored locally (for
              example, in your browser&apos;s storage or your device&apos;s
              secure storage). Profile details such as your role (e.g., Staff,
              Principal, Parent, Admin), display name, and school details (e.g.
              school name) are stored and fetched from our servers.
            </li>
            <li>
              <strong>Photos and Media</strong>: When staff capture photos with
              the in-app camera or select them from the device&apos;s gallery,
              they may be temporarily stored in the app&apos;s cache or local
              filesystem on the device. After a successful upload or if the
              upload is cancelled, the app is designed to remove working copies
              from its own local storage so that teachers&apos; personal phones
              are not used as long-term storage for school photos. Photos that
              already exist in your device&apos;s camera roll remain under your
              own control.
            </li>
            <li>
              <strong>Stories, Hashtags and Galleries</strong>: When you add
              stories, captions, class labels, or hashtags to photos, this
              content is sent to our servers and linked to the relevant photo,
              school, and user. We use this to organise galleries (for example,
              per class, event or school hashtag) for staff, principals and
              parents.
            </li>
            <li>
              <strong>Drafts</strong>: If you work offline or save your work,
              drafts (including photos and associated data such as stories or
              class labels) may be stored locally on the device. When the app is
              able to upload successfully, the drafts are uploaded to our
              servers and the app removes its local working copies. Drafts are
              not intended for long-term storage and may be automatically cleaned
              up after a period to avoid unnecessary use of device storage.
            </li>
            <li>
              <strong>Device and Usage Information</strong>: We collect basic
              technical information such as network status (online/offline) and
              general app usage information to manage offline functionality and
              improve performance. Additional device details (for example,
              device type, operating system, or crash/error reports) may be
              collected by third-party services where used (see{" "}
              <em>Third-Party Services</em> below).
            </li>
            <li>
              <strong>Billing and Subscription Information</strong>: For
              Principals or schools that choose a paid subscription, we may
              process limited billing-related information (for example, a
              reference to your Stripe customer ID) via our payment processor.
              Full payment card details are handled by Stripe and not stored on
              our own servers.
            </li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">
            Cookies and Similar Technologies
          </h2>
          <p>
            On the Ghrianghraffy website and web dashboard, we may use cookies
            and similar technologies to:
          </p>
          <ul>
            <li>Keep you signed in and secure your session.</li>
            <li>Remember your preferences (for example, interface settings).</li>
            <li>
              Understand, in aggregate, how the service is used so we can
              improve it (for example, basic analytics).
            </li>
          </ul>
          <p>
            You can usually control cookies through your browser settings.
            Disabling certain cookies may affect core functionality such as
            logging in or staying signed in.
          </p>
          {/* If you start using specific tools like Google Analytics or ad networks,
              you can expand this section to name them explicitly. */}
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ul>
            <li>
              Authenticate your account and provide access to role-specific
              features (e.g., photo uploads for Staff/Principal, viewing for
              Parents).
            </li>
            <li>
              Process photos, stories, class labels and hashtags so they can be
              uploaded, reviewed, organised into galleries, and made available
              to authorised users in your school community.
            </li>
            <li>
              Manage drafts, retries and offline behaviour so photos are
              uploaded when possible, and removed from the app&apos;s local
              storage once they are no longer needed.
            </li>
            <li>
              Improve app functionality and user experience based on network
              status and usage data, and to keep the service reliable and secure.
            </li>
            <li>
              Communicate with you about your account, including security
              notifications, service updates, and important information about
              your trial or subscription status.
            </li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Data Storage and Security</h2>
          <ul>
            <li>
              <strong>On Your Device</strong>: Photos and drafts are stored only
              as long as needed for upload or offline use. The app uses its own
              local storage or cache and deletes working copies after upload or
              cancellation to minimise storage of school images on personal
              devices. Authentication tokens and basic profile details (such as
              school name) may be stored locally to keep you signed in, using
              mechanisms such as browser storage or secure storage on the
              device.
            </li>
            <li>
              <strong>On Our Servers</strong>: Uploaded photos, stories, profile
              data, and logs are stored on our servers and served via{" "}
              <a href="https://ghrianghraffy.com">
                https://ghrianghraffy.com
              </a>{" "}
              and related API endpoints over HTTPS. We apply reasonable
              technical and organisational measures to protect this data against
              unauthorised access, loss, or misuse.
            </li>
            <li>
              <strong>Retention and Trials</strong>: We keep personal data only
              for as long as necessary to provide the service, fulfil the
              purposes described in this Privacy Policy, and meet our legal
              obligations. For example, data associated with trial accounts may
              be deleted (including photos and related accounts for that school)
              after the trial ends if no subscription is started, in line with
              our product design. When a school is an active paying customer,
              we retain data while the subscription remains in place, unless the
              school instructs us to remove certain content sooner.
            </li>
            <li>
              While we work hard to protect your information, no system can be
              guaranteed 100% secure. You should keep your login details
              confidential and let us or your school know if you suspect
              unauthorised access.
            </li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Data Sharing</h2>
          <p>We do not sell your personal information. We may share information:</p>
          <ul>
            <li>
              Within your school community, as part of normal use of the app
              (for example, staff uploading photos for Principals to review, or
              parents viewing approved photos for their school or class).
            </li>
            <li>
              With service providers who help us operate the app (such as
              hosting, email delivery, payment processing, or optional
              analytics/error-logging providers), under appropriate contracts and
              data protection safeguards. For example, we may use Stripe to
              handle subscription payments, and email providers to send
              registration or reset-password emails.
            </li>
            <li>
              With third-party services your school chooses to connect, such as
              posting selected approved photos to the school&apos;s official
              Instagram account, or using our plugin/API to display approved
              photos on the school&apos;s own website.
            </li>
            <li>
              To comply with legal obligations or to protect our rights and the
              safety of users.
            </li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Third-Party Services</h2>
          <p>
            We may use trusted third-party service providers to help deliver and
            improve Ghrianghraffy. These may include:
          </p>
          <ul>
            <li>Cloud hosting and infrastructure providers.</li>
            <li>Email delivery or communication services.</li>
            <li>Payment processors (for example, Stripe) for subscriptions.</li>
            <li>
              Optional integrations such as Instagram or school website
              platforms (for example, WordPress) where a school chooses to
              connect them.
            </li>
            <li>
              Optional analytics or error-logging tools to understand how the
              service is used and to monitor reliability.
            </li>
          </ul>
          <p>
            These providers only receive the information necessary to perform
            their services and are required to handle your data securely and in
            line with applicable data protection laws. They are not allowed to
            use your information for their own independent marketing purposes.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Your Rights</h2>
          <p>
            Depending on your region (for example, under GDPR for EU/UK users or
            under other local laws), you may have rights to access, correct, or
            delete your personal data, or to object to or restrict certain types
            of processing. You can exercise these rights by contacting us at{" "}
            <a href="mailto:privacy@ghrianghraffy.com">
              privacy@ghrianghraffy.com
            </a>
            , or by contacting your school as the data controller for pupil
            photos and related content.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">GDPR and Data Protection</h2>
          <p>
            For users in the European Economic Area (EEA) and the United
            Kingdom, we aim to process personal data in accordance with the
            General Data Protection Regulation (&quot;GDPR&quot;) and applicable
            local data protection laws.
          </p>

          <h3 className="privacy-subheading">Our role</h3>
          <ul>
            <li>
              For account information (for example, your login details, contact
              email and billing data), Ghrianghraffy acts as a{" "}
              <strong>data controller</strong>.
            </li>
            <li>
              For pupil photos and related content uploaded by schools and
              authorised staff, we generally act as a{" "}
              <strong>data processor</strong> on behalf of the school, which is
              the data controller. We process this data only according to the
              school&apos;s instructions and our agreements with them.
            </li>
          </ul>

          <h3 className="privacy-subheading">Legal bases for processing</h3>
          <p>
            We rely on one or more of the following legal bases to process your
            personal data:
          </p>
          <ul>
            <li>
              <strong>Contract</strong>: where processing is necessary to
              provide our services to you or your school (for example, to create
              and manage accounts, or allow you to log in and use the app).
            </li>
            <li>
              <strong>Legitimate interests</strong>: where we or your school
              have a legitimate interest that is not overridden by your rights
              and interests (for example, securing our systems, preventing
              abuse, or improving the service, or for the school to document and
              communicate school life with families).
            </li>
            <li>
              <strong>Legal obligation</strong>: where we must process or retain
              data to comply with legal requirements (for example, tax or
              accounting rules).
            </li>
            <li>
              <strong>Consent</strong>: in limited cases where consent is used
              as a legal basis (for example, where schools gather parental
              consent for use of pupil images under their own policies). In
              those cases, consent is normally managed by the school, and can be
              withdrawn according to the school&apos;s process.
            </li>
          </ul>

          <h3 className="privacy-subheading">Your GDPR rights</h3>
          <p>
            Under GDPR, you may have the right to request access to your
            personal data, rectify inaccurate data, request deletion, restrict or
            object to certain processing, and (in some cases) request data
            portability. You also have the right to lodge a complaint with your
            local data protection authority.
          </p>
          <p>
            You can exercise these rights by contacting us at{" "}
            <a href="mailto:privacy@ghrianghraffy.com">
              privacy@ghrianghraffy.com
            </a>
            . We may need to verify your identity before responding to your
            request. For pupil-related content, we may ask you to first contact
            your school, and we will work with them as needed.
          </p>

          <h3 className="privacy-subheading">Data transfers</h3>
          <p>
            If we transfer personal data outside of the EEA/UK (for example, to
            service providers), we will take appropriate steps to protect it,
            such as using the EU/UK Standard Contractual Clauses or relying on
            other lawful transfer mechanisms where required.
          </p>

          <h3 className="privacy-subheading">Retention</h3>
          <p>
            We keep personal data only for as long as necessary to provide the
            service, fulfil the purposes described in this Privacy Policy, and
            meet our legal obligations. For trial accounts that do not convert
            to a subscription, we may delete the trial school&apos;s photos,
            related accounts (such as staff and parent logins) and other
            school-specific configuration after the trial period ends. For
            active subscriber schools, we retain data while the subscription
            continues, unless we are instructed by the school to remove specific
            content.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Children’s Privacy</h2>
          <p>
            Ghrianghraffy is intended to be used by schools, their authorised
            staff, and parents. The service may involve photos of children, but
            we do not offer accounts directly to children or allow children to
            sign up themselves.
          </p>
          <p>
            Schools and authorised staff are responsible for ensuring they have
            the appropriate legal basis (for example, consent or another lawful
            ground under local law) to capture and upload photos of pupils. We
            process this data on behalf of the school in line with this Privacy
            Policy and our agreements with the school.
          </p>
          <p>
            If you believe we have collected personal information directly from
            a child without proper authorisation, please contact us at{" "}
            <a href="mailto:privacy@ghrianghraffy.com">
              privacy@ghrianghraffy.com
            </a>{" "}
            so we can investigate and take appropriate action.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect
            changes in our practices, technologies, or legal requirements. If we
            make significant changes, we will notify you via the app, our
            website, or email where appropriate. The latest version will always
            be available on this page.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy or how we handle
            personal data, contact us at{" "}
            <a href="mailto:privacy@ghrianghraffy.com">
              privacy@ghrianghraffy.com
            </a>
            .
          </p>
        </section>
      </div>
    </div>
  );
};

export default Privacy;
